// client/src/components/pages/Settings/LexiStarWebhooks/webhookLogic.ts
//
// Pure logic of Settings → Integrations & webhooks (ST-00207; M4b contracts §3): the event
// catalogue the picker offers (every type of @lexistar/domain EVENT_CATALOGUE, M1–M5 — a test
// keeps the two equal), the pattern rules (`*`, exact CATEGORY.NAME, CATEGORY.PREFIX* — the
// server's and @lexistar/messaging's rule), and the labels of delivery states and responses.
//
// ST-00280: the catalogue, its category names and the type words come from the one label
// catalogue (utils/eventTypeLabels.ts); a type is shown in words, its code beside it.

import type {
  WebhookDeliveryState,
  WebhookDeliverySummary,
} from '../../../../types/lexistar-webhooks';
import {
  CATALOGUE_EVENT_TYPES,
  EVENT_CATEGORY_LABELS,
  EVENT_CATEGORY_NOUNS,
  eventTypePhrase,
} from '../../../../utils/eventTypeLabels';

/** @lexistar/domain EVENT_CATALOGUE (M1 + M2 + M3 + M4 + M5), in its order (utils/eventTypeLabels). */
export const WEBHOOK_EVENT_TYPES: readonly string[] = CATALOGUE_EVENT_TYPES;

/** Plain-language names of the event categories, in picker order (utils/eventTypeLabels). */
export const CATEGORY_LABELS: Readonly<Record<string, string>> = EVENT_CATEGORY_LABELS;

/** The category as an adjective in a sentence: "all trip events", "all weighbridge events". */
export const CATEGORY_NOUNS: Readonly<Record<string, string>> = EVENT_CATEGORY_NOUNS;

export const categoryOf = (type: string): string => type.split('.')[0] ?? type;

/** The categories of the catalogue, in CATEGORY_LABELS order (unknown ones last). */
export const EVENT_CATEGORIES: readonly string[] = (() => {
  const present = [...new Set(WEBHOOK_EVENT_TYPES.map(categoryOf))];
  const order = Object.keys(CATEGORY_LABELS);
  return present.sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
})();

export const typesOf = (category: string): string[] =>
  WEBHOOK_EVENT_TYPES.filter((t) => categoryOf(t) === category);

/** `*`, an exact CATEGORY.NAME or CATEGORY.PREFIX* — as the server validates. */
export const PATTERN_RE = /^(\*|[A-Z]+\.[A-Z_]*\*|[A-Z]+\.[A-Z_]+)$/;
export const isPattern = (p: string): boolean => p.length <= 100 && PATTERN_RE.test(p);

/** Does `type` match one of the patterns (`*`, exact, trailing-`*` prefix)? */
export function matches(patterns: readonly string[], type: string): boolean {
  return patterns.some(
    (p) => p === '*' || p === type || (p.endsWith('*') && type.startsWith(p.slice(0, -1)))
  );
}

/** Catalogue types a pattern set covers. */
export const coveredTypes = (patterns: readonly string[]): string[] =>
  WEBHOOK_EVENT_TYPES.filter((t) => matches(patterns, t));

/**
 * Canonical pattern set: `*` alone when present; globs and exact types a broader glob already
 * covers dropped (ROUTE.* covers ROUTE.DEVIATION_* and ROUTE.DEVIATION_OPENED); duplicates
 * removed; order: globs first, then types in catalogue order, then the rest.
 */
export function normalizePatterns(patterns: readonly string[]): string[] {
  const clean = [...new Set(patterns.map((p) => p.trim()).filter(Boolean))];
  if (clean.includes('*')) return ['*'];
  const globs = clean
    .filter((p) => p.endsWith('*'))
    .filter((g, _i, all) => !all.some((o) => o !== g && g.startsWith(o.slice(0, -1))))
    .sort();
  const exact = clean.filter((p) => !p.endsWith('*') && !matches(globs, p));
  const rank = (t: string): number => {
    const i = WEBHOOK_EVENT_TYPES.indexOf(t);
    return i < 0 ? WEBHOOK_EVENT_TYPES.length : i;
  };
  exact.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  return [...globs, ...exact];
}

/**
 * A short sentence of what a subscription receives: "Every event", "All trip events and
 * weighbridge ticket matched to a trip" (a single type in words, a prefix pattern as typed).
 */
export function describePatterns(patterns: readonly string[], max = 3): string {
  const set = normalizePatterns(patterns);
  if (set.length === 0) return 'No events';
  if (set[0] === '*') return 'Every event';
  const words = set.map((p) => {
    const m = /^([A-Z]+)\.\*$/.exec(p);
    if (m) return `all ${CATEGORY_NOUNS[m[1] as string] || (m[1] as string)} events`;
    return p.endsWith('*') ? p : eventTypePhrase(p);
  });
  const shown = words.slice(0, max);
  const rest = words.length - shown.length;
  const list =
    rest > 0
      ? `${shown.join(', ')} and ${rest} more`
      : shown.length > 1
        ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
        : (shown[0] as string);
  return list.charAt(0).toUpperCase() + list.slice(1);
}

// ── delivery states ─────────────────────────────────────────────────────────

/** Attempts before a delivery is dead (the first + 5 retries). */
export const MAX_ATTEMPTS = 6;

export type StateTone = 'good' | 'wait' | 'warn' | 'bad';

export interface StateMeta {
  label: string;
  tone: StateTone;
  /** A glyph besides colour (colour is never the only signal). */
  glyph: string;
}

export function deliveryState(d: Pick<WebhookDeliverySummary, 'state' | 'attempt'>): StateMeta {
  switch (d.state) {
    case 'delivered':
      return { label: 'Delivered', tone: 'good', glyph: '✓' };
    case 'dead':
      return { label: 'Dead', tone: 'bad', glyph: '✖' };
    case 'failed':
      return { label: 'Failed', tone: 'bad', glyph: '✖' };
    default:
      return d.attempt > 0
        ? { label: `Retrying (${d.attempt} of ${MAX_ATTEMPTS} failed)`, tone: 'warn', glyph: '↻' }
        : { label: 'Queued', tone: 'wait', glyph: '◌' };
  }
}

export const STATE_FILTERS: ReadonlyArray<{ value: '' | WebhookDeliveryState; label: string }> = [
  { value: '', label: 'Every state' },
  { value: 'pending', label: 'Queued or retrying' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'dead', label: 'Dead' },
  { value: 'failed', label: 'Failed' },
];

/** "204 · 180 ms", "HTTP 500 · 2.1 s", "timeout after 10 s", or "—". */
export function responseText(
  d: Pick<WebhookDeliverySummary, 'response_code' | 'response_ms' | 'error' | 'state'>
): string {
  const ms = d.response_ms;
  const took =
    ms === null || ms === undefined
      ? ''
      : ms >= 1000
        ? ` · ${(ms / 1000).toFixed(1)} s`
        : ` · ${ms} ms`;
  if (d.state === 'delivered' && d.response_code !== null) return `${d.response_code}${took}`;
  if (d.error)
    return `${d.error}${d.response_code !== null && !/^HTTP /.test(d.error) ? ` (${d.response_code})` : ''}${took}`;
  if (d.response_code !== null) return `HTTP ${d.response_code}${took}`;
  return '—';
}

export const kindLabel = (kind: string): string | null =>
  kind === 'ping' ? 'Test' : kind === 'replay' ? 'Replay' : null;

/** The endpoint for display: host + path (the query string may hold a token: it is cut). */
export function endpointLabel(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    return `${u.host}${path}${u.search ? '?…' : ''}`;
  } catch {
    return url;
  }
}

/** Client-side check of the URL before the server's (which also resolves the host). */
export function urlProblem(url: string): string | null {
  const text = url.trim();
  if (!text) return 'Enter the URL of your endpoint';
  let u: URL;
  try {
    u = new URL(text);
  } catch {
    return 'This is not a valid URL';
  }
  if (u.protocol !== 'https:') return 'Only https:// URLs are allowed';
  if (u.username || u.password) return 'The URL must not contain a user name or password';
  if (!u.hostname.includes('.') && !u.hostname.startsWith('[')) return 'Use a public domain name';
  return null;
}
